/**
 * 牌桌嘴炮的纯函数：冷却、未读、「催一下」的可用状态、历史行、气泡时长。
 *
 * 全部不碰 store / DOM / 时钟 —— 时间一律由调用方传进来，node 里可以逐条钉死。
 * 「催一下」的判据与服务端 `onNudge` 逐条对应（服务端是最终裁决，这里只是提前拦一道、
 * 决定按钮什么时候出现），数字取同一份 `TALK` 常量。
 */
import { TALK, nudgeWaitMs } from '@poker/shared'
import type { AccountId, ChatEntry, HandId, Millis, PlayerView, SeatIndex } from '@poker/shared'

// ============================================================================
// 冷却（客户端镜像服务端的两个令牌桶）
// ============================================================================

/** 打字：1 条 / 2s。返回还要等多少毫秒（0 = 现在就能发） */
export function textCooldownMs(lastTextAt: number | null, now: number): number {
  if (lastTextAt === null) return 0
  return Math.max(0, TALK.CHAT_MIN_INTERVAL_MS - (now - lastTextAt))
}

/** 短语桶此刻剩几枚令牌（容量 PHRASE_BURST，每秒回 PHRASE_PER_SEC）。与服务端 TokenBucket 同一算法。 */
export function phraseTokens(stamps: readonly number[], now: number): number {
  const cap: number = TALK.PHRASE_BURST
  const perMs = TALK.PHRASE_PER_SEC / 1000
  let tokens = cap
  let last: number | null = null
  for (const t of stamps) {
    if (last !== null) tokens = Math.min(cap, tokens + (t - last) * perMs)
    tokens -= 1
    last = t
  }
  if (last === null) return cap
  return Math.min(cap, tokens + (now - last) * perMs)
}

export function phraseCooldownMs(stamps: readonly number[], now: number): number {
  const tokens = phraseTokens(stamps, now)
  if (tokens >= 1) return 0
  return Math.ceil(((1 - tokens) * 1000) / TALK.PHRASE_PER_SEC)
}

/** 记一次短语发送；只留算令牌需要的那几条 */
export function pushStamp(stamps: readonly number[], now: number): number[] {
  return [...stamps, now].slice(-TALK.PHRASE_BURST * 2)
}

// ============================================================================
// 历史与未读
// ============================================================================

export interface TalkLine {
  readonly id: string
  readonly who: string
  readonly mine: boolean
  readonly text: string
  readonly phrase: boolean
  readonly system: boolean
}

export function talkLines(chat: readonly ChatEntry[], me: AccountId | null, limit: number): TalkLine[] {
  return chat.slice(-limit).map((e) => ({
    id: e.id,
    who: e.kind === 'system' ? '' : e.accountId !== null && e.accountId === me ? '我' : e.nickname,
    mine: e.accountId !== null && e.accountId === me,
    text: e.text,
    phrase: e.phrase !== undefined,
    system: e.kind === 'system',
  }))
}

/**
 * 未读 = 比「上次看到」更新、且不是自己说的话。
 * 用时间戳而不是 id 记「看到哪了」：客户端只留 30 条，id 被挤出去之后就对不上了。
 */
export function unreadCount(chat: readonly ChatEntry[], me: AccountId | null, seenAt: number | null): number {
  if (seenAt === null) return 0
  let n = 0
  for (const e of chat) {
    if (e.at <= seenAt) continue
    if (e.kind !== 'player') continue
    if (me !== null && e.accountId === me) continue
    n++
  }
  return n
}

export function latestAt(chat: readonly ChatEntry[]): number {
  return chat.length === 0 ? 0 : (chat[chat.length - 1]?.at ?? 0)
}

// ============================================================================
// 催一下
// ============================================================================

export function nudgeKey(handId: HandId, actionNo: number): string {
  return `${handId}:${String(actionNo)}`
}

export type NudgeState =
  | { readonly kind: 'none' }
  | {
      /** wait = 还没想够；ready = 能催；done = 这一步我催过了；closed = 倒计时最后几秒 */
      readonly kind: 'wait' | 'ready' | 'done' | 'closed'
      readonly seat: SeatIndex
      readonly name: string
      readonly key: string
      readonly readyAt: Millis
      readonly closeAt: Millis
    }

const NONE: NudgeState = { kind: 'none' }

/**
 * 此刻能不能催、催谁、什么时候能催。`now` 是**服务端时间**（`link.clock.now()`）。
 *
 * `none` 与其余几种的区别：`none` 时按钮位置都不该占（没人可催），
 * `wait` / `closed` 时按钮隐形但占位 —— 到点亮起时不会把别的东西挤到手指下。
 */
export function nudgeState(view: PlayerView | null, now: Millis, nudgedKey: string | null): NudgeState {
  const hand = view?.hand ?? null
  if (view === null || hand === null || hand.result !== null) return NONE
  const actor = hand.actorSeat
  if (actor === null || hand.actorSince === null || hand.displayDeadlineAt === null) return NONE
  const mine = view.viewer.ownedSeat
  if (mine === null || mine === actor) return NONE
  const seat = view.seats[actor]
  const who = seat?.occupant ?? null
  if (seat === undefined || who === null || who.isBot || seat.presence !== 'online') return NONE

  const toCall = Math.max(0, hand.betToMatch - seat.committedThisRound)
  const wait = nudgeWaitMs(toCall, seat.stack, toCall > 0 && toCall >= seat.stack)
  const readyAt = hand.actorSince + wait
  const closeAt = hand.displayDeadlineAt - TALK.NUDGE_CLOSE_BEFORE_DEADLINE_MS
  const key = nudgeKey(hand.handId, hand.actionNo)
  const base = { seat: actor, name: who.nickname, key, readyAt, closeAt }
  if (nudgedKey === key) return { kind: 'done', ...base }
  if (now < readyAt) return { kind: 'wait', ...base }
  if (now > closeAt) return { kind: 'closed', ...base }
  return { kind: 'ready', ...base }
}

/** 下一次状态会变的时刻（按钮据此只挂一个定时器，不做倒计时 —— 红线 #48） */
export function nextNudgeChangeAt(s: NudgeState, now: Millis): Millis | null {
  if (s.kind === 'wait') return s.readyAt
  if (s.kind === 'ready') return s.closeAt + 1
  void now
  return null
}

// ============================================================================
// 气泡
// ============================================================================

export type SayKind = 'text' | 'phrase' | 'nudge'

/** 停留时长：短语 2.4s；催一下 2s；手打的按长短 2.4–4.8s */
export function sayHoldMs(kind: SayKind, text: string): number {
  if (kind === 'phrase') return 2400
  if (kind === 'nudge') return 2000
  return Math.min(4800, Math.max(2400, 2400 + 60 * [...text].length))
}

/**
 * 服务端时间比这老的社交事件不冒气泡、不震动、不进快讯：
 * 断线补发的帧是「新到的」，但内容可能是半分钟前的。
 */
export function isFreshEvent(serverTimeOfFrame: Millis, serverNow: Millis): boolean {
  return serverNow - serverTimeOfFrame <= TALK.EVENT_MAX_AGE_MS
}

/** 这一手还在打（手牌中发完话要把牌桌还给你；等待室里不收，方便连着聊） */
export function handLive(view: PlayerView | null): boolean {
  const hand = view?.hand ?? null
  return hand !== null && hand.result === null && hand.phase !== 'complete'
}
